// Scaffolded Investigation — React port of kula-create's
// scaffolded-investigation.html.
//
// Pedagogy: three rounds of decreasing scaffolding. Round 1 fully guided
// (preset probes only), Round 2 mixed (some preset + custom), Round 3
// fully independent. The escalation builds inquiry skill.
//
// Per-round response: { roundId, probes: string[], reflection: string }.

import { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ScaffoldedInvestigationSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: ScaffoldedInvestigationSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ScaffoldedInvestigationSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function ScaffoldedInvestigationSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ScaffoldedInvestigationSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // Only the last round's response payload is persisted, and one round's
  // {roundId, probes, reflection} can't reconstruct mid-investigation
  // progress — so only a saved FINAL round restores (as all-done); anything
  // else starts fresh.
  const finalRoundId = data.rounds.length > 0 ? data.rounds[data.rounds.length - 1].id : null;
  const restored =
    savedResponse && finalRoundId !== null && savedResponse.roundId === finalRoundId ? savedResponse : null;
  const [currentRoundIdx, setCurrentRoundIdx] = useState(restored ? data.rounds.length - 1 : 0);
  const [customProbes, setCustomProbes] = useState<Record<string, string[]>>(() =>
    restored && finalRoundId !== null && Array.isArray(restored.probes)
      ? { [finalRoundId]: restored.probes.filter((p): p is string => typeof p === 'string') }
      : {},
  );
  const [revealedPresets, setRevealedPresets] = useState<Record<string, Set<number>>>({});
  const [reflections, setReflections] = useState<Record<string, string>>(() =>
    restored && finalRoundId !== null && typeof restored.reflection === 'string'
      ? { [finalRoundId]: restored.reflection }
      : {},
  );
  const [completedRounds, setCompletedRounds] = useState<Set<string>>(() =>
    restored ? new Set(data.rounds.map((r) => r.id)) : new Set(),
  );
  const [pauseRemaining, setPauseRemaining] = useState(0);
  const completedRef = useRef(restored !== null);

  const currentRound = data.rounds[currentRoundIdx];
  const allDone = data.rounds.length > 0 && completedRounds.size === data.rounds.length;

  // Reading pause: when a round becomes current, lock its inputs for
  // readingPauseSeconds with a visible countdown. Skipped when 0/undefined.
  useEffect(() => {
    const secs = currentRound && !allDone ? currentRound.readingPauseSeconds ?? 0 : 0;
    if (!secs || secs <= 0) {
      setPauseRemaining(0);
      return;
    }
    setPauseRemaining(secs);
    const interval = setInterval(() => {
      setPauseRemaining((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoundIdx, allDone]);

  const inputsLocked = pauseRemaining > 0;

  const updateCustom = (roundId: string, idx: number, value: string) => {
    setCustomProbes((prev) => {
      const next = [...(prev[roundId] ?? [])];
      next[idx] = value;
      return { ...prev, [roundId]: next };
    });
  };

  // Reveals are tracked by probe index, not tag — duplicate tags would
  // collide React keys and reveal together.
  const revealPreset = (roundId: string, probeIdx: number) =>
    setRevealedPresets((prev) => ({
      ...prev,
      [roundId]: new Set([...(prev[roundId] ?? []), probeIdx]),
    }));

  const isRoundReady = (round: ScaffoldedInvestigationSlideData['rounds'][number]) => {
    const probes = customProbes[round.id] ?? [];
    const validCustom = probes.filter((p) => p && p.trim().length > 0).length;
    const refl = reflections[round.id] ?? '';
    return validCustom >= round.customProbesRequired && refl.trim().length > 0;
  };

  const completeRound = useCallback(() => {
    if (!currentRound) return;
    const probes = (customProbes[currentRound.id] ?? []).filter((p) => p && p.trim().length > 0);
    onActivityResponse?.({
      activityType: 'scaffolded-investigation',
      mode: 'survey',
      responseData: {
        roundId: currentRound.id,
        probes,
        reflection: reflections[currentRound.id] ?? '',
      },
    });
    setCompletedRounds((prev) => new Set(prev).add(currentRound.id));
    if (currentRoundIdx < data.rounds.length - 1) {
      setCurrentRoundIdx((i) => i + 1);
    } else if (!completedRef.current) {
      // Final round done — the debrief is display-only, so this is completion.
      completedRef.current = true;
      onComplete?.();
    }
  }, [currentRound, currentRoundIdx, customProbes, reflections, data.rounds.length, onActivityResponse, onComplete]);

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {data.rounds.length === 0 && (
          <div
            className="rounded-lg p-6 text-center border"
            style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
          >
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              This investigation has no rounds yet — regenerate it via the AI build to add content.
            </p>
          </div>
        )}

        {/* Round indicator */}
        <div className="flex items-center justify-center gap-3 mb-6 text-xs">
          {data.rounds.map((r, idx) => {
            const isActive = idx === currentRoundIdx && !allDone;
            const isDone = completedRounds.has(r.id);
            return (
              <div key={r.id} className="flex items-center gap-2">
                <span
                  className="px-3 py-1.5 rounded-full font-semibold transition-colors duration-300"
                  style={{
                    background: isActive ? accent : isDone ? `${accent}25` : theme.colors.surface,
                    color: isActive ? '#fff' : isDone ? accent : theme.colors.textMuted,
                  }}
                >
                  Round {r.roundNumber} · {r.mode}
                </span>
                {idx < data.rounds.length - 1 && (
                  <span style={{ color: theme.colors.textMuted }}>→</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Completed rounds — collapsed, read-only review */}
        {data.rounds.map((round) =>
          completedRounds.has(round.id) ? (
            <details
              key={round.id}
              className="rounded-lg border mb-3"
              style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
            >
              <summary className="px-4 py-3 cursor-pointer text-sm font-semibold" style={{ color: theme.colors.text }}>
                Round {round.roundNumber} · {round.title} ✓
              </summary>
              <div className="px-4 pb-4 space-y-3 text-sm">
                {round.presetProbes.some((_, i) => revealedPresets[round.id]?.has(i)) && (
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>
                      Probes revealed
                    </p>
                    {round.presetProbes.map((probe, i) =>
                      revealedPresets[round.id]?.has(i) ? (
                        <div key={i} className="mb-2">
                          <p className="font-medium" style={{ color: theme.colors.text }}>{probe.question}</p>
                          <p className="text-xs" style={{ color: theme.colors.textMuted }}>{probe.response}</p>
                        </div>
                      ) : null,
                    )}
                  </div>
                )}
                {(customProbes[round.id] ?? []).some((p) => p && p.trim().length > 0) && (
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>
                      Your probes
                    </p>
                    {(customProbes[round.id] ?? [])
                      .filter((p) => p && p.trim().length > 0)
                      .map((p, i) => (
                        <p key={i} style={{ color: theme.colors.text }}>{p}</p>
                      ))}
                  </div>
                )}
                {(reflections[round.id] ?? '').trim().length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>
                      Reflection
                    </p>
                    <p style={{ color: theme.colors.text }}>{reflections[round.id]}</p>
                  </div>
                )}
              </div>
            </details>
          ) : null,
        )}

        {/* Current round panel — keyed by round index so escalating to the next
            round remounts and animates the panel in. No <AnimatePresence
            mode="wait">: Reveal is a plain (non-forwardRef) component, so the
            exiting round would block the next from mounting and freeze the
            panel on an invisible card. */}
        {currentRound && !allDone && (
            <Reveal
              key={currentRoundIdx}
              variant="up"
              className="rounded-lg p-5 border"
              style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
            >
              <h3 className="font-semibold mb-2" style={{ color: theme.colors.text }}>{currentRound.title}</h3>
              <div className="leading-relaxed text-sm mb-4" style={{ color: theme.colors.text }}>
                {currentRound.content}
              </div>

              {inputsLocked && (
                <div
                  className="rounded p-3 mb-4 text-xs font-semibold text-center"
                  style={{ background: `${accent}15`, color: accent }}
                >
                  Read first — inputs unlock in {pauseRemaining}s
                </div>
              )}

              {/* Preset probes — click to reveal */}
              {currentRound.presetProbes.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                    Preset probes ({currentRound.presetProbes.length})
                  </p>
                  <div className="space-y-2">
                    {currentRound.presetProbes.map((probe, probeIdx) => {
                      const revealed = revealedPresets[currentRound.id]?.has(probeIdx);
                      return (
                        <div key={probeIdx} className="rounded p-3" style={{ background: theme.colors.background }}>
                          <p className="text-sm font-medium" style={{ color: theme.colors.text }}>{probe.question}</p>
                          <AnimatePresence mode="wait" initial={false}>
                            {revealed ? (
                              <Reveal key="answer" variant="up">
                                <p className="text-xs mt-2" style={{ color: theme.colors.textMuted }}>{probe.response}</p>
                              </Reveal>
                            ) : (
                              <button
                                key="toggle"
                                onClick={() => revealPreset(currentRound.id, probeIdx)}
                                disabled={inputsLocked}
                                className="text-xs mt-2"
                                style={{ color: accent, opacity: inputsLocked ? 0.4 : 1 }}
                              >
                                Reveal the answer
                              </button>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom probes — author N independent questions */}
              {currentRound.customProbesRequired > 0 && (
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                    Your probes ({currentRound.customProbesRequired} required)
                  </p>
                  {Array.from({ length: currentRound.customProbesRequired }, (_, i) => (
                    <input
                      key={i}
                      value={customProbes[currentRound.id]?.[i] ?? ''}
                      disabled={inputsLocked}
                      onChange={(e) => updateCustom(currentRound.id, i, e.target.value)}
                      placeholder={`Probe ${i + 1}…`}
                      className="w-full mb-2 px-3 py-2 rounded border text-sm"
                      style={{
                        background: theme.colors.background,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    />
                  ))}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                  Reflection
                </label>
                <textarea
                  value={reflections[currentRound.id] ?? ''}
                  disabled={inputsLocked}
                  onChange={(e) => setReflections((prev) => ({ ...prev, [currentRound.id]: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded border text-sm"
                  style={{
                    background: theme.colors.background,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
              </div>

              <button
                onClick={completeRound}
                disabled={!isRoundReady(currentRound) || inputsLocked}
                className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                style={{
                  background: accent,
                  color: '#fff',
                  cursor: isRoundReady(currentRound) && !inputsLocked ? 'pointer' : 'not-allowed',
                  opacity: isRoundReady(currentRound) && !inputsLocked ? 1 : 0.4,
                }}
              >
                {currentRoundIdx < data.rounds.length - 1 ? 'Continue to next round' : 'Complete investigation'}
              </button>
            </Reveal>
          )}

        {/* Final debrief — payoff register (heavier accent rule + larger radius).
            Always renders once every round is done so the learner lands a clear
            terminal beat: the authored debrief prompt when present, else a
            fallback line confirming completion (a sparse activity with no debrief
            authored must not finish on an empty screen). */}
        <AnimatePresence>
          {allDone && (
            <Reveal
              variant="scale"
              spring="bouncy"
              className="rounded-2xl p-5 border-l-[5px] shadow-sm mt-4 text-center"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Debrief</p>
              <p className="text-sm" style={{ color: theme.colors.textMuted }}>
                {data.debrief?.prompt?.trim()
                  ? data.debrief.prompt
                  : `Investigation complete — you worked through all ${data.rounds.length} round${data.rounds.length === 1 ? '' : 's'}, each with less scaffolding than the last.`}
              </p>
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ScaffoldedInvestigationSlideData;
  theme: Theme;
  onUpdate: (data: ScaffoldedInvestigationSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.debrief?.prompt ?? ''}
          onChange={(e) => onUpdate({ ...data, debrief: { prompt: e.target.value } })}
          placeholder="Debrief prompt"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.rounds.length} rounds · escalating from {data.rounds[0]?.mode ?? '—'} to {data.rounds[data.rounds.length - 1]?.mode ?? '—'}.
          Structural edits (rounds, probes) are AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
